<template>
  <div class="min-h-screen flex items-center justify-center bg-[#232323]">
    <!-- Yellow background card -->
    <div class="absolute w-[70vw] h-[80vh] bg-[#F9C73D] rounded-2xl transform rotate-3 top-20 left-30 z-0"></div>

    <!-- Background image card -->
    <div
      class="absolute w-[70vw] h-[80vh] bg-cover bg-center rounded-2xl top-20 left-30 z-10"
      :style="{ backgroundImage: `url(${require('@/assets/Login/loginbg.png')})` }"
    >
      <!-- Top-left logo -->
      <router-link to="/" :class="['absolute top-6 z-20', $i18n?.locale === 'ar' ? 'right-6' : 'left-6']">
        <img src="@/assets/logo.png" alt="Logo" class="w-[64px] h-[64px] sm:w-[80px] sm:h-[80px] md:w-[96px] md:h-[96px] lg:w-[106px] lg:h-[106px] object-contain cursor-pointer" />
      </router-link>
    </div>

    <!-- Main content card -->
    <div class="relative z-20 bg-[#232323] rounded-2xl px-12 py-10 flex flex-col items-center w-[600px] shadow-[0_0_25px_6px_rgba(255,255,255,0.4)]">
      <!-- Envelope Image -->
      <img src="@/assets/icons/otp.svg" alt="Envelope" class="w-28 h-24 mb-6" />

      <!-- Title and Message -->
      <h2 class="text-2xl font-bold text-[#F9C73D] mb-2 text-center">
        {{ $t('verifyEmail.title') || 'Verify Your Email' }}
      </h2>
      <p class="text-white text-center mb-8">
        An email has been sent to your inbox. Please check your email and click the verification link to complete your account setup.
      </p>

      <!-- Resend + Login -->
      <div class="flex gap-4 w-full">
        <button
          type="button"
          @click="resendVerification"
          class="flex-1 bg-[#F9C73D] text-black font-semibold py-3 rounded transition hover:bg-yellow-400 hover:text-white hover:shadow-[0_0_20px_rgba(249,199,61,0.6)]"
          :disabled="resendCooldown > 0"
        >
          {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Email' }}
        </button>

        <router-link
          to="/login"
          class="flex-1 bg-[#F9C73D] text-black font-semibold py-3 rounded transition hover:bg-yellow-400 hover:text-white hover:shadow-[0_0_20px_rgba(249,199,61,0.6)] text-center flex items-center justify-center"
        >
          Sign in
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import {
  getAuth,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';

export default {
  name: 'VerifyEmail',
  setup() {
    const auth = getAuth();
    const router = useRouter();

    const user = ref(null);
    const isResending = ref(false);
    const resendCooldown = ref(0);
    let cooldownTimer = null;
    let verificationCheckInterval = null;

    // Watch the user's auth state
    onMounted(() => {
      onAuthStateChanged(auth, (u) => {
        if (u) {
          user.value = u;

          // Start checking if user gets verified
          startVerificationCheck();
        }
      });
    });

    onBeforeUnmount(() => {
      if (cooldownTimer) clearInterval(cooldownTimer);
      if (verificationCheckInterval) clearInterval(verificationCheckInterval);
    });

    // Send email again
    const resendVerification = async () => {
      if (!user.value || isResending.value || resendCooldown.value > 0) return;

      try {
        isResending.value = true;
        await sendEmailVerification(user.value);
        startCooldown();
      } catch (error) {
        alert('Error sending verification email: ' + error.message);
      } finally {
        isResending.value = false;
      }
    };

    // Disable resend for 60 seconds
    const startCooldown = () => {
      resendCooldown.value = 60;
      cooldownTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0 && cooldownTimer) {
          clearInterval(cooldownTimer);
        }
      }, 1000);
    };

    // Auto-check every few seconds if the user is verified
    const startVerificationCheck = () => {
      verificationCheckInterval = setInterval(async () => {
        await auth.currentUser.reload(); // Refresh user data
        if (auth.currentUser.emailVerified) {
          clearInterval(verificationCheckInterval);
          router.push('/login');
        }
      }, 3000); // check every 3 seconds
    };

    return {
      resendVerification,
      resendCooldown,
    };
  },
};
</script>
